import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  message,
  Space,
  Modal,
  Form,
  Input,
  Tooltip,
  Row,
  Tag,
  Empty,
  Radio,
  Checkbox
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  UndoOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons';
import { membershipService } from '../../../services';
import dayjs from "dayjs";
import {ExtendedDelegateResponse} from "../../../model/props/changeFlag";
import {
  DelegatesTabProps,
  MembershipDelegateCreateItem,
  MembershipDelegateDeleteItem,
  MembershipDelegateUpdateItem
} from "../../../model";
import EmailFormItem from "../../../components/common/EmailFormItem";
import RocDateInputPicker from "../../../components/shared/RocDateInputPicker";



const { confirm } = Modal;



const DelegatesTab: React.FC<DelegatesTabProps> = ({
  membershipId,
  onDataChanged,
  changedFields,
  onSaveSuccess,
  isEditing,
}) => {
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState<ExtendedDelegateResponse[]>([]);
  const [delegateData, setDelegateData] = useState<ExtendedDelegateResponse[]>([]);
  const [modifiedDelegates, setModifiedDelegates] = useState<{[key: number]: boolean}>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentDelegate, setCurrentDelegate] = useState<ExtendedDelegateResponse | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const userAccount = localStorage.getItem('userAccount') ?? '';

  // 可選擇的職稱
  /* const positionOptions = [
    '董事長', '總經理', '副總經理', '協理', '經理', '課長', '專員', '其他'
  ]; */

  // 載入會員代表資料
  const fetchDelegateData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await membershipService.getDelegates({ membershipId });
      if (data && Array.isArray(data)) {
        setDelegateData(data);
        setOriginalData(JSON.parse(JSON.stringify(data))); // 深拷貝保存原始資料
        setModifiedDelegates({});
        setHasChanges(false);
        // 通知父元件已無變更
        onDataChanged('delegates', false);
      } else {
        setDelegateData([]);
        setOriginalData([]);
        message.info('沒有找到會員代表資料');
      }
    } catch (error) {
      console.error('Failed to fetch delegate data:', error);
      message.error('載入會員代表資料時發生錯誤');
      setDelegateData([]);
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  }, [membershipId, onDataChanged]);

  useEffect(() => {
    fetchDelegateData();
  }, [fetchDelegateData]);

  // 判斷代表是否已修改
  const isDelegateModified = (record: ExtendedDelegateResponse) => {
    return !!modifiedDelegates[record.membershipDelegateId];
  };
  
  // 代表是否被標記為刪除
  const isDelegateDeleted = (record: ExtendedDelegateResponse) => {
    return !!record._deleted;
  };
  
  // 代表是否為新增
  const isDelegateNew = (record: ExtendedDelegateResponse) => {
    return record.membershipDelegateId < 0 || !!record._new;
  };

  // 新增會員代表
  const handleAddDelegate = async () => {
    if (!isEditing) return;
    
    try {
      const values = await addForm.validateFields();
      
      // 格式化生日日期
      const formattedBirthday = values.birthday ? 
        dayjs(values.birthday).format('YYYY-MM-DD') : undefined;
      
      // 將新代表加入至暫存資料中
      const newDelegate: ExtendedDelegateResponse = {
        membershipDelegateId: -Date.now(), // 臨時负值 ID，用來識別新增項目
        membershipId: membershipId,
        name: values.name,
        idNumber: values.idNumber,
        gender: values.gender,
        birthday: formattedBirthday,
        birthPlace: values.birthPlace,
        position: values.position,
        email: values.email,
        phone: values.phone,
        isCongressDelegate: values.isCongressDelegate || false,
        _new: true
      };
      
      const newData = [...delegateData, newDelegate];
      setDelegateData(newData);
      
      // 標記為已修改
      setModifiedDelegates(prev => ({
        ...prev,
        [newDelegate.membershipDelegateId]: true
      }));
      setHasChanges(true);
      
      // 通知上層組件有資料變更
      onDataChanged('delegates', true);
      
      // setIsAddModalVisible(false);
      addForm.resetFields();
      message.success('已添加會員代表，點擊儲存按鈕以儲存變更');
    } catch (error) {
      console.error('Add delegate failed:', error);
      message.error('新增會員代表時發生錯誤');
    }
  };

  // 更新會員代表
  const handleUpdateDelegate = async () => {
    if (!currentDelegate || !isEditing) return;
    
    try {
      const values = await editForm.validateFields();
      
      // 格式化生日日期
      const formattedBirthday = values.birthday ? 
        dayjs(values.birthday).format('YYYY-MM-DD') : undefined;
      
      // 在暫存資料中更新代表
      const newData = delegateData.map(item => {
        if (item.membershipDelegateId === currentDelegate.membershipDelegateId) {
          return {
            ...item,
            name: values.name,
            idNumber: values.idNumber,
            gender: values.gender,
            birthday: formattedBirthday,
            birthPlace: values.birthPlace,
            position: values.position,
            email: values.email,
            phone: values.phone,
            isCongressDelegate: values.isCongressDelegate || false
          };
        }
        return item;
      });
      
      setDelegateData(newData);
      
      // 標記為已修改
      setModifiedDelegates(prev => ({
        ...prev,
        [currentDelegate.membershipDelegateId]: true
      }));
      setHasChanges(true);
      
      // 通知上層組件有資料變更
      onDataChanged('delegates', true);
      
      setIsEditModalVisible(false);
      setCurrentDelegate(null);
      editForm.resetFields();
      message.success('已更新會員代表，點擊儲存按鈕以儲存變更');
    } catch (error) {
      console.error('Update delegate failed:', error);
      message.error('更新會員代表時發生錯誤');
    }
  };

  // 刪除會員代表
  const handleDeleteDelegate = (record: ExtendedDelegateResponse) => {
    if (!isEditing) return;
    
    // 如果是新增的項目（负數 ID）
    if (record.membershipDelegateId < 0) {
      // 直接從暫存資料中移除
      setDelegateData(delegateData.filter(item => 
        item.membershipDelegateId !== record.membershipDelegateId
      ));
      
      // 從修改記錄中移除
      const updatedModified = { ...modifiedDelegates };
      delete updatedModified[record.membershipDelegateId];
      setModifiedDelegates(updatedModified);
      
      // 更新變更狀態
      const hasRemainingChanges = Object.keys(updatedModified).length > 0;
      setHasChanges(hasRemainingChanges);
      onDataChanged('delegates', hasRemainingChanges);
      
      message.success('已移除會員代表');
      return;
    }
    
    // 將現有代表標記為待刪除
    setDelegateData(prevData => 
      prevData.map(item => 
        item.membershipDelegateId === record.membershipDelegateId
          ? { ...item, _deleted: true }
          : item
      )
    );
    
    // 標記為已修改
    setModifiedDelegates(prev => ({
      ...prev,
      [record.membershipDelegateId]: true
    }));
    setHasChanges(true);
    
    // 通知上層組件有資料變更
    onDataChanged('delegates', true);
    
    message.success('已標記會員代表為刪除，點擊儲存按鈕以完成刪除');
  };

  // 確認刪除會員代表
  const confirmDelete = (record: ExtendedDelegateResponse) => {
    if (!isEditing) return;
    
    confirm({
      title: '確定要刪除此會員代表嗎？',
      icon: <ExclamationCircleFilled />,
      content: `將會移除會員代表：${record.name}`,
      okText: '確定',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        handleDeleteDelegate(record);
      },
    });
  };

  // 開啟編輯視窗
  const showEditModal = (record: ExtendedDelegateResponse) => {
    if (!isEditing) return;
    
    // 將日期字串轉為 dayjs 對象
    const birthday = record.birthday ? dayjs(record.birthday) : null;
    
    setCurrentDelegate(record);
    editForm.setFieldsValue({
      ...record,
      birthday
    });
    setIsEditModalVisible(true);
  };
  
  // 儲存所有變更
  const handleSaveChanges = async () => {
    if (!hasChanges || !isEditing) {
      message.info('沒有需要儲存的變更');
      return;
    }
    
    setLoading(true);
    
    try {
      // 備份原本資料，用於失敗時還原
      const originalDelegateDataBackup = JSON.parse(JSON.stringify(delegateData));
      
      // 准備要新增的會員代表
      const delegatesToAdd = delegateData
        .filter(p => p.membershipDelegateId < 0 || p._new) // 新增的代表有臨時负值ID或_new標記
        .map(p => ({
          membershipId,
          seq: p.seq,
          name: p.name,
          idNumber: p.idNumber,
          gender: p.gender,
          birthday: p.birthday,
          birthPlace: p.birthPlace,
          position: p.position,
          email: p.email,
          phone: p.phone,
          isCongressDelegate: p.isCongressDelegate,
          createdBy: userAccount
        }) as MembershipDelegateCreateItem);
      
      // 准備要更新的會員代表
      const delegatesToUpdate = delegateData
        .filter(p => p.membershipDelegateId > 0 && // 正常 ID
                  modifiedDelegates[p.membershipDelegateId] && // 已修改 
                  !p._deleted && // 不是待刪除
                  !p._new) // 不是新增項目
        .map(p => ({
          membershipDelegateId: p.membershipDelegateId,
          membershipId,
          seq: p.seq,
          name: p.name,
          idNumber: p.idNumber,
          gender: p.gender,
          birthday: p.birthday,
          birthPlace: p.birthPlace,
          position: p.position,
          email: p.email,
          phone: p.phone,
          isCongressDelegate: p.isCongressDelegate,
          modifiedBy: userAccount
        }) as MembershipDelegateUpdateItem);
      
      // 准備要刪除的會員代表
      const delegatesToDelete = delegateData
        .filter(p => p.membershipDelegateId > 0 && p._deleted) // 被標記為刪除的現有代表
        .map(p => ({
          membershipDelegateId: p.membershipDelegateId
        }) as MembershipDelegateDeleteItem);
      
      // 执行 API 請求
      let isSuccess = true;
      let errorMsg = '';
      
      // 新增會員代表
      if (delegatesToAdd.length > 0) {
        try {
          const createResponse = await membershipService.createDelegates({
            membershipDelegates: delegatesToAdd
          });
          
          if (!createResponse || createResponse.createdCount === 0) {
            isSuccess = false;
            errorMsg = '新增會員代表失敗';
          }
        } catch (error) {
          console.error('Create delegates failed:', error);
          isSuccess = false;
          errorMsg = '新增會員代表時發生錯誤';
        }
      }
      
      // 更新會員代表
      if (isSuccess && delegatesToUpdate.length > 0) {
        try {
          const updateResponse = await membershipService.updateDelegates({
            membershipDelegates: delegatesToUpdate
          });
          
          if (!updateResponse || updateResponse.updatedCount === 0) {
            isSuccess = false;
            errorMsg = '更新會員代表失敗';
          }
        } catch (error) {
          console.error('Update delegates failed:', error);
          isSuccess = false;
          errorMsg = '更新會員代表時發生錯誤';
        }
      }
      
      // 刪除會員代表
      if (isSuccess && delegatesToDelete.length > 0) {
        try {
          const deleteResponse = await membershipService.deleteDelegates({
            membershipDelegateIds: delegatesToDelete,
            modifiedBy: userAccount
          });
          
          if (!deleteResponse || deleteResponse.deletedCount === 0) {
            isSuccess = false;
            errorMsg = '刪除會員代表失敗';
          }
        } catch (error) {
          console.error('Delete delegates failed:', error);
          isSuccess = false;
          errorMsg = '刪除會員代表時發生錯誤';
        }
      }
      
      if (isSuccess) {
        message.success('所有會員代表變更已儲存成功');
        // 重新載入最新資料
        fetchDelegateData();
        onSaveSuccess();
      } else {
        // 還原原始資料
        setDelegateData(originalDelegateDataBackup);
        message.error(errorMsg || '儲存會員代表變更失敗');
      }
    } catch (error) {
      console.error('Save delegates failed:', error);
      message.error('儲存會員代表變更時發生錯誤');
      // 還原原始資料
      handleResetChanges();
    } finally {
      setLoading(false);
    }
  };
  
  // 重置變更
  const handleResetChanges = () => {
    setDelegateData(JSON.parse(JSON.stringify(originalData)));
    setModifiedDelegates({});
    setHasChanges(false);
    onDataChanged('delegates', false);
    message.info('已返回原始資料');
  };

  // 獲取表格欄位定義
  const getColumns = () => {
    // 基本欄位
    const baseColumns = [
      {
        title: '序號',
        dataIndex: 'seq',
        key: 'seq',
        width: 80,
        render: (text: number, record: ExtendedDelegateResponse) => {
          const displayText = text || '-';
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{displayText}</span>;
          }
          return isDelegateModified(record) ? 
            <span style={{ color: '#f50' }}>{displayText}</span> : 
            displayText;
        }
      },
      {
        title: '代表性名',
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: ExtendedDelegateResponse) => {
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text}</span>;
          }
          return isDelegateModified(record) ? 
            <span style={{ color: '#f50' }}>{text}</span> : 
            text;
        }
      },
      {
        title: '身分證字號',
        dataIndex: 'idNumber',
        key: 'idNumber',
        render: (text: string, record: ExtendedDelegateResponse) => {
          const displayText = text || '-';
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{displayText}</span>;
          }
          return isDelegateModified(record)
            ? <span style={{ color: '#f50' }}>{displayText}</span>
            : displayText;
        }
      },
      {
        title: '性別',
        dataIndex: 'gender',
        key: 'gender',
        render: (value: string, record: ExtendedDelegateResponse) => {
          const displayValue = value === '1' ? '男' : value === '2' ? '女' : '-';
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{displayValue}</span>;
          }
          return isDelegateModified(record)
            ? <span style={{ color: '#f50' }}>{displayValue}</span>
            : displayValue;
        }
      },
      {
        title: '生日',
        dataIndex: 'birthday',
        key: 'birthday',
        render: (text: string, record: ExtendedDelegateResponse) => {
          const displayText = text || '-';
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{displayText}</span>;
          }
          return isDelegateModified(record)
            ? <span style={{ color: '#f50' }}>{displayText}</span>
            : displayText;
        }
      },
      {
        title: '出生地',
        dataIndex: 'birthPlace',
        key: 'birthPlace',
        render: (text: string, record: ExtendedDelegateResponse) => {
          const displayText = text || '-';
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{displayText}</span>;
          }
          return isDelegateModified(record)
            ? <span style={{ color: '#f50' }}>{displayText}</span>
            : displayText;
        }
      },
      {
        title: '職務',
        dataIndex: 'position',
        key: 'position',
        render: (text: string, record: ExtendedDelegateResponse) => {
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text || '-'}</span>;
          }
          return isDelegateModified(record) ? 
            <span style={{ color: '#f50' }}>{text || '-'}</span> : 
            (text || '-');
        }
      },
      {
        title: '大會代表',
        dataIndex: 'isCongressDelegate',
        key: 'isCongressDelegate',
        width: 100,
        render: (value: boolean, record: ExtendedDelegateResponse) => {
          const displayValue = value ? '是' : '否';
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{displayValue}</span>;
          }
          return isDelegateModified(record) ? 
          <span style={{ color: '#f50' }}>{displayValue}</span> : 
          displayValue;
        }
      },
      {
        title: '電子郵件',
        dataIndex: 'email',
        key: 'email',
        render: (text: string, record: ExtendedDelegateResponse) => {
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text || '-'}</span>;
          }
          return isDelegateModified(record) ? 
            <span style={{ color: '#f50' }}>{text || '-'}</span> : 
            (text || '-');
        }
      },
      {
        title: '手機',
        dataIndex: 'phone',
        key: 'phone',
        render: (text: string, record: ExtendedDelegateResponse) => {
          if (isDelegateDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text || '-'}</span>;
          }
          return isDelegateModified(record) ? 
            <span style={{ color: '#f50' }}>{text || '-'}</span> : 
            (text || '-');
        }
      }
    ];
    
    // 只在編輯模式下添加額外的欄位
    if (isEditing) {
      return [
        ...baseColumns,
        {
          title: '狀態',
          key: 'status',
          width: 100,
          render: (_: any, record: ExtendedDelegateResponse) => {
            if (isDelegateDeleted(record)) {
              return <Tag color="red">待刪除</Tag>;
            }
            if (isDelegateNew(record)) {
              return <Tag color="green">新增</Tag>;
            }
            if (isDelegateModified(record)) {
              return <Tag color="orange">已修改</Tag>;
            }
            return null;
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          render: (_: any, record: ExtendedDelegateResponse) => {
            if (isDelegateDeleted(record)) {
              return (
                <Button 
                  type="link" 
                  onClick={() => {
                    // 取消刪除標記
                    setDelegateData(prevData => 
                      prevData.map(item => 
                        item.membershipDelegateId === record.membershipDelegateId
                          ? { ...item, _deleted: false }
                          : item
                      )
                    );
                    // 使用時間戳創建新的修改記錄，以確保觸發狀態更新
                    setModifiedDelegates(prev => {
                      const newModified = { ...prev };
                      delete newModified[record.membershipDelegateId];
                      return newModified;
                    });
                    // 更新變更狀態
                    setHasChanges(true);
                    onDataChanged('delegates', true);
                  }}
                >
                  取消刪除
                </Button>
              );
            }
            
            return (
              <Space size="small">
                <Tooltip title="編輯">
                  <Button 
                    type="text" 
                    icon={<EditOutlined />} 
                    onClick={() => showEditModal(record)} 
                    disabled={isDelegateDeleted(record)}
                  />
                </Tooltip>
                <Tooltip title="刪除">
                  <Button 
                    type="text" 
                    icon={<DeleteOutlined />} 
                    danger 
                    onClick={() => confirmDelete(record)}
                    disabled={isDelegateDeleted(record)}
                  />
                </Tooltip>
              </Space>
            );
          },
        }
      ];
    }
    
    return baseColumns;
  };

  // 創建或編輯會員代表的表單
  const renderDelegateForm = (form: any) => (
    <>
      <Form.Item
        name="name"
        label="姓名"
        rules={[{ required: true, message: '請輸入姓名' }]}
      >
        <Input placeholder="請輸入姓名" maxLength={50} />
      </Form.Item>
      
      <Form.Item
        name="idNumber"
        label="身分證字號"
      >
        <Input placeholder="請輸入身分證字號（選填）" maxLength={20} />
      </Form.Item>
      
      <Form.Item
        name="gender"
        label="性別"
      >
        <Radio.Group>
          <Radio value="1">男</Radio>
          <Radio value="2">女</Radio>
        </Radio.Group>
      </Form.Item>
      
      <Form.Item
        name="birthday"
        label="生日"
      >
        {/*  <ROCDatePicker*/}
        {/*      placeholder="請選擇生日（選填）"*/}
        {/*      style={{ width: '100%' }}*/}
        {/*  />*/}
          <RocDateInputPicker />
      </Form.Item>
      
      <Form.Item
        name="birthPlace"
        label="出生地"
      >
        <Input placeholder="請輸入出生地（選填）" maxLength={50} />
      </Form.Item>

      <Form.Item
        name="position"
        label="職稱"
      >
        <Input placeholder="請選擇職稱（選填）" />
      </Form.Item>

      <EmailFormItem
        name="email"
        label="電子郵件"
        rules={[
          {
            type: 'email',
            message: '請輸入有效的電子郵件地址'
          }
        ]}
      >
        <Input placeholder="請輸入電子郵件（選填）" maxLength={100} />
      </EmailFormItem>
      
      <Form.Item
        name="phone"
        label="電話"
      >
        <Input placeholder="請輸入電話（選填）" maxLength={20} />
      </Form.Item>
      
      <Form.Item
        name="isCongressDelegate"
        valuePropName="checked"
      >
        <Checkbox>會員大會代表</Checkbox>
      </Form.Item>
    </>
  );

  const renderTable = () => {
    if (!delegateData || delegateData.length === 0) {
      return (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
          description="尚無會員代表資料" 
        />
      );
    }
    
    return (
      <Table
        sticky
        rowKey="membershipDelegateId"
        columns={getColumns()}
        dataSource={delegateData.filter(item => !isDelegateDeleted(item) || isEditing)}
        loading={loading}
        pagination={false}
        rowClassName={(record) => isDelegateModified(record) ? 'modified-row' : ''}
      />
    );
  };

  return (
    <Card>
      {isEditing && (
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setIsAddModalVisible(true)}
            >
              新增會員代表
            </Button>
          </Space>
          
          <Space>
            <Button 
              icon={<UndoOutlined />} 
              onClick={handleResetChanges}
              disabled={!hasChanges}
            >
              重置
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={handleSaveChanges}
              loading={loading}
              disabled={!hasChanges}
            >
              儲存變更
            </Button>
          </Space>
        </Row>
      )}

      {renderTable()}

      {/* 新增會員代表視窗 */}
      <Modal
        title="新增會員代表"
        open={isAddModalVisible}
        onOk={handleAddDelegate}
        onCancel={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
        }}
        okText="新增"
        cancelText="關閉"
        confirmLoading={loading}
        width={600}
      >
        <Form form={addForm} layout="vertical">
          {renderDelegateForm(addForm)}
        </Form>
      </Modal>

      {/* 編輯會員代表視窗 */}
      <Modal
        title="編輯會員代表"
        open={isEditModalVisible}
        onOk={handleUpdateDelegate}
        onCancel={() => {
          setIsEditModalVisible(false);
          setCurrentDelegate(null);
          editForm.resetFields();
        }}
        okText="更新"
        cancelText="取消"
        confirmLoading={loading}
        width={600}
      >
        <Form form={editForm} layout="vertical">
          {renderDelegateForm(editForm)}
        </Form>
      </Modal>
    </Card>
  );
};

export default DelegatesTab;
